<template>
  <div
    class="app-tile"
    :class="{
      'app-tile_disabled': !userHasPermission || appStore.isNavigating,
    }"
  >
    <router-link
      :to="{
        name: appListMap[app.appId].route as any,
      }"
      class="app-tile__link"
      :style="`border-color: ${appTileColorMap[appStore.getAppGroup(app)]};`"
    >
      <div class="app-tile__content">
        <component
          :is="getAppIcon(app.appId)"
          class="app-tile__content-icon"
        />
        <div class="app-tile__content-text">
          <div
            class="typo-font-3 app-tile__title"
            style="color: var(--text-color);"
          >
            {{ app.displayName }}
          </div>
          <div
            class="typo-small"
            style="color: var(--color-text-secondary);"
          >
            {{ i18n.global.t(`api.AppGroupCommercial.${app.appGroupCommercial}`) }}
          </div>
        </div>
        <PrmTag
          v-if="doesExpireSoon"
          v-tooltip="{
            value: doesExpireSoon && appLicenseExpiryDayjs ? i18n.global.t(`${I18N_PREFIX}.expiresSoon`, {
              expiryDate: formatDayjsDate(appLicenseExpiryDayjs),
              appName: app.displayName,
            }) : undefined,
          }"
          severity="warning"
          icon="pi pi-exclamation-triangle"
        />
      </div>
    </router-link>

    <PinToggleButton
      v-if="userHasPermission"
      class="app-tile__pin-button"
      tabindex="-1"
      :pinned="isAppPinned"
      :pin-tooltip="i18n.global.t(`${I18N_PREFIX}.pinApp`)"
      :unpin-tooltip="i18n.global.t(`${I18N_PREFIX}.unpinApp`)"
      :disabled="customUserStore.customPinnedAppListIsLoading"
      @update:pinned="customUserStore.toggleCustomPinnedApp(app.appId)"
    />
  </div>
</template>

<script lang="ts" setup>
import PrmTag from '@NitrexGroup/primevue/tag';
import dayjs from '@dayjs';
import {
  i18n,
} from '@i18n';
import {
  isNullish,
} from '@qmulus/ui/utils/common.ts';
import {
  computed,
} from 'vue';

import PinToggleButton from '@/components/pin-toggle-button/pin-toggle-button.vue';
import {
  useAppStore,
} from '@/store/pinia/app.ts';
import {
  useCustomUserStorageStore,
} from '@/store/pinia/custom-user-storage.ts';
import {
  useLicenseStore,
} from '@/store/pinia/license.ts';
import {
  EAppId,
  TApp,
} from '@/types/app-type.ts';
import {
  appListMap,
  getAppIcon,
} from '@/types/app-util.ts';
import {
  appTileColorMap,
} from '@/utils/app-tile.ts';
import {
  formatDayjsDate,
} from '@/utils/date-formats.ts';

const I18N_PREFIX = 'components/app-tile/app-tile';

const appStore = useAppStore();
appStore.loadCurrentUser();
const customUserStore = useCustomUserStorageStore();
customUserStore.loadCustomPinnedAppList();
const licenseStore = useLicenseStore();

const props = defineProps<{
  app: TApp,
}>();

const isAppPinned = computed(() => customUserStore.customPinnedAppList?.includes(props.app.appId) ?? false);

const userHasPermission = computed(() => {
  // Settings app contains restricted and unrestricted sections and has appID TENANT_SETTINGS
  // user should be able to always access unrestricted section (user settings)
  // thus we create an exception for this appID
  if (props.app.appId === EAppId.TENANT_SETTINGS) {
    return true;
  }
  return !!appStore.user?.appPermissions?.[props.app.appId];
});

const appLicense = computed(() => {
  if (isNullish(licenseStore.license)) {
    return undefined;
  }
  return licenseStore.license.apps.find((appLicense) => appLicense.appId === props.app.appId);
});

const appLicenseExpiryDayjs = computed(() => {
  if (isNullish(appLicense.value)) {
    return undefined;
  }
  if (appLicense.value.expiry === 'unlimited') {
    return undefined;
  }
  return dayjs(appLicense.value.expiry);
});

const doesExpireSoon = computed(() => {
  if (isNullish(appLicenseExpiryDayjs.value) || isNullish(appLicense.value)) {
    return false;
  }
  const showWarningAfter = appLicenseExpiryDayjs.value.subtract(appLicense.value.expiryWarningDays || 0, 'days');
  return dayjs().isAfter(showWarningAfter);
});
</script>

<style lang="scss" scoped>
@use "@qmulus/ui/styles/variables/color";

$horz-gap: var(--gap-union);

.app-tile {
  position: relative;
  width: 100%;
  height: 3.875rem;
  border-radius: var(--border-radius-small);
  overflow: hidden;
  border: 1px solid var(--border-color-subtle);
  background-color: var(--surface-card);
  display: flex;
  justify-content: space-between;

  // The tile clips the focus outline of its link, so the whole tile shows that the link has the focus.
  &:has(.app-tile__link:focus-visible) {
    box-shadow: var(--focus-shadow);
  }

  &_disabled {
    cursor: not-allowed;
    pointer-events: none;
    opacity: 0.5;
  }

  &__link {
    display: flex;
    padding: var(--gap);
    justify-content: space-between;
    border-left: 0.75rem solid;
    flex-grow: 1;

    &:focus-visible {
      outline: none;
    }
  }

  &__pin-button {
    padding: var(--gap);
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
  }

  &__content {
    height: 100%;
    display: flex;
    align-items: center;
    gap: $horz-gap;
  }

  &__content-icon {
    width: 1.875rem;
    height: 1.875rem;
    display: flex;
    justify-content: center;
    align-items: center;
    color: var(--text-color);
  }

  &__content-text {
    height: 100%;
    display: flex;
    flex-direction: column;
    justify-content: center;
  }

  &__title {
    font-family: var(--font-family-heading);
    font-synthesis-weight: none;
  }
}
</style>
